import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, isRef as _isRef, vModelDynamic as _vModelDynamic, mergeProps as _mergeProps, withDirectives as _withDirectives, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "px-5 py-3 bg-white border border-gray-200 shadow rounded-lg" }
const _hoisted_2 = { class: "text-gray-900 text-xl font-bold mb-2" }
const _hoisted_3 = { class: "block text-sm leading-6 text-gray-900" }
const _hoisted_4 = { class: "px-5 py-3 bg-white border flex flex-col gap-2 border-gray-200 shadow sm:rounded-lg" }
const _hoisted_5 = { class: "text-gray-900 text-xl font-bold" }
const _hoisted_6 = { class: "relative rounded-md bg-white py-1.5 pr-1.5 pl-3 shadow-sm ring-1 ring-inset ring-gray-300 w-full group/filter-box" }
const _hoisted_7 = { class: "flex items-center justify-between" }
const _hoisted_8 = { class: "font-normal mb-1" }
const _hoisted_9 = { class: "text-sm flex items-center" }
const _hoisted_10 = { class: "mr-3 text-gray-400" }
const _hoisted_11 = {
  key: 0,
  class: "flex justify-between text-gray-500"
}
const _hoisted_12 = { class: "max-w-[160px] overflow-hidden whitespace-nowrap text-ellipsis mr-1" }
const _hoisted_13 = { key: 1 }
const _hoisted_14 = { class: "relative rounded-md bg-white py-1.5 pl-3 pr-1.5 shadow-sm ring-1 ring-inset ring-gray-300 w-full group/filter-box" }
const _hoisted_15 = { class: "flex items-center justify-between" }
const _hoisted_16 = { class: "font-normal mb-1" }
const _hoisted_17 = { class: "text-sm flex items-center" }
const _hoisted_18 = { class: "mr-3 text-gray-400" }
const _hoisted_19 = {
  key: 0,
  class: "flex justify-between"
}
const _hoisted_20 = { class: "text-gray-500 max-w-[160px] overflow-hidden whitespace-nowrap text-ellipsis mr-1" }
const _hoisted_21 = { class: "text-gray-500" }
const _hoisted_22 = { key: 1 }
const _hoisted_23 = { class: "px-5 py-3 bg-white border border-gray-200 shadow sm:rounded-lg" }
const _hoisted_24 = { class: "text-gray-900 text-xl font-bold mb-2" }

import { XMarkIcon } from "@heroicons/vue/24/outline";
import { processClasses } from "@oculai/planner";
import deepEqual from "deep-equal";
import { useForm } from "vee-validate";
import { PropType, ref, watch } from "vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";
import ProcessLocationFilters from "@/components/analytics/filters/Location.vue";
import { HIERARCHY_TAGS_META } from "@/constants/hierarchyTags";
import { HierarchyTagStore, HierarchyType } from "@/types/HierarchyTag";
import { ProcessReport, ResourceGroup } from "@/types/Process";
import ProcessPicker from "../ProcessPicker.vue";
import CycleTimesConfig from "./CycleTimesConfig.vue";
import WorkingHourCurveConfig from "./WorkingHourCurveConfig.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'Form',
  props: {
  hierarchyTags: {
    type: Array as PropType<HierarchyTagStore[]>,
    required: true,
  },
  selectedReport: {
    type: Object as PropType<ProcessReport | null>,
    default: null,
  },
},
  emits: ["onSave", "onDelete", "onChange", "onRefetch"],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t } = useI18n();

const props = __props;

const emits = __emit;

const cycleTimesConfigComponent = ref();
const workingHourCurveConfigComponent = ref();
const selectedProcesses = ref<ProcessReport["processes"]>({
  mode: "component",
  components: [],
  single_processes: [],
});
const availableHierarchyTags = ref<Exclude<HierarchyType, "component">[]>([]);
const selectedSectionMasks = ref({
  building: new Array<string>(),
  level: new Array<string>(),
  section: new Array<string>(),
});

const tagGroups = ref<ResourceGroup[]>([]);

const schema = object({
  name: string().required(t("analytics.reports.validation_name_required")),
});

const { errors, defineField } = useForm({
  initialValues: { name: "" },
  validationSchema: schema,
});

const [nameFieldModel, nameFieldAttr] = defineField("name");

const mapIds = (item: string | { _id: string }) => (typeof item === "string" ? item : item._id);

const getAvailableHierarchyTags = (tags: HierarchyTagStore[]) => {
  return Array.from(new Set(tags.map((tag) => tag.type)))
    .filter((tag) => tag !== "component")
    .sort((a, b) => HIERARCHY_TAGS_META[a].order - HIERARCHY_TAGS_META[b].order) as Exclude<
    HierarchyType,
    "component"
  >[];
};

const remapHierarchyTagsToTagPickerGroups = (tags: HierarchyTagStore[]) => {
  tags.sort((a, b) => a.number - b.number);

  const tagsGroups = tags
    .reduce((acc, tag) => {
      if (tag.type === "component") {
        return acc;
      }

      const group = acc.find((item) => item.field === tag.type);

      if (group) {
        group.options.push({ name: tag.name, value: tag._id });
      } else {
        acc.push({
          name: t(`analytics.reports.${tag.type}`),
          field: tag.type,
          options: [{ name: tag.name, value: tag._id }],
        });
      }

      return acc;
    }, [] as ResourceGroup[])
    .sort((a, b) => HIERARCHY_TAGS_META[a.field].order - HIERARCHY_TAGS_META[b.field].order);

  return tagsGroups;
};

watch(
  () => props.selectedReport,
  (report) => {
    if (!report) {
      return;
    }

    nameFieldModel.value = report.name;

    availableHierarchyTags.value = getAvailableHierarchyTags(props.hierarchyTags);
    selectedProcesses.value = report.processes;
    tagGroups.value = remapHierarchyTagsToTagPickerGroups(props.hierarchyTags);

    selectedSectionMasks.value = {
      building: report.building.map(mapIds),
      level: report.level.map(mapIds),
      section: report.section.map(mapIds),
    };
  },
  { immediate: true },
);

const selectedProcessesLabel = computed(() => {
  if (!selectedProcesses.value) {
    return "";
  }

  if (selectedProcesses.value.mode === "component") {
    return selectedProcesses.value.components
      .map(({ id, processes }) => {
        const component = populateSectionMask(id);

        if (!component) {
          return "";
        }

        if (processes.length === component.attached_processes.length) {
          return component.name;
        }

        const selectedProcessElements = component.attached_processes.map((process) => {
          return processClasses.find((processClass) => processClass.encodedLabel === process)
            ?.processElementLabel;
        });

        const processElementsLabel = Array.from(new Set(selectedProcessElements))
          .map((label) => t(`report.sections.processes.process_type_entries.${label}`))
          .join(", ");

        return `${component.name} (${processElementsLabel})`;
      })
      .join(", ");
  }

  return selectedProcesses.value.single_processes
    .map((id) => t(`report.sections.processes.process_type_entries.${id}`))
    .join(", ");
});

const populateSectionMask = (id: string) => {
  const tag = props.hierarchyTags.find((tag) => tag._id === id);

  return tag;
};

const getConfig = () => {
  if (props.selectedReport?.plot.type === "working_hour_curve") {
    return workingHourCurveConfigComponent.value?.getConfig();
  } else if (props.selectedReport?.plot.type === "cycle_times") {
    return cycleTimesConfigComponent.value?.getConfig();
  }

  return {};
};

const handleProcessesChange = (customProcesses: ProcessReport["processes"]) => {
  const config = getConfig();

  selectedProcesses.value = customProcesses;

  handleReportRefetch(config);
};

const handleSectionMasksChange = (sectionMasks: typeof selectedSectionMasks.value) => {
  const config = getConfig();

  selectedSectionMasks.value = sectionMasks;

  handleReportRefetch(config);
};

const getFormValues = () => {
  return {
    ...props.selectedReport,
    name: nameFieldModel.value,
    processes: selectedProcesses.value,
    building: selectedSectionMasks.value.building,
    level: selectedSectionMasks.value.level,
    section: selectedSectionMasks.value.section,
    plot: {
      type: props.selectedReport?.plot.type,
      config: getConfig(),
    },
  };
};

const handleSubmit = () => {
  const values = getFormValues();

  emits("onSave", values);
};

const handleReportChange = (config: Record<string, unknown>) => {
  emits("onChange", {
    name: nameFieldModel.value,
    processes: selectedProcesses.value,
    building: selectedSectionMasks.value.building,
    level: selectedSectionMasks.value.level,
    section: selectedSectionMasks.value.section,
    plot: {
      type: props.selectedReport?.plot.type,
      config,
    },
  });
};

const handleReportRefetch = (config: Record<string, unknown>) => {
  handleReportChange(config);

  emits("onRefetch", {
    processes: selectedProcesses.value,
    building: selectedSectionMasks.value.building,
    level: selectedSectionMasks.value.level,
    section: selectedSectionMasks.value.section,
    ...config,
  });
};

const handleRemoveFilter = (name: "processes" | "section_masks") => {
  const config = getConfig();
  let shouldRefetch = false;

  if (
    (name === "processes" &&
      !deepEqual(selectedProcesses.value, {
        mode: "component",
        components: [],
        single_processes: [],
      })) ||
    (name === "section_masks" &&
      Object.values(selectedSectionMasks.value).some((masks) => masks.length))
  ) {
    shouldRefetch = true;
  }

  if (name === "processes") {
    selectedProcesses.value = { mode: "component", components: [], single_processes: [] };
  } else {
    selectedSectionMasks.value = {
      building: [],
      level: [],
      section: [],
    };
  }

  if (shouldRefetch) {
    handleReportRefetch(config);
  }
};

__expose({
  handleSubmit,
  getValues: getFormValues,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: _normalizeClass(['flex flex-col gap-4', !__props.selectedReport && 'invisible'])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)("analytics.reports.general")), 1),
      _createElementVNode("label", null, [
        _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)("analytics.reports.name")), 1),
        _withDirectives(_createElementVNode("input", _mergeProps({
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(nameFieldModel) ? (nameFieldModel).value = $event : null))
        }, _unref(nameFieldAttr), {
          class: [
            'mb-4 outline-none block w-full rounded-md border-0 py-1.5 pl-3 ring-1 ring-inset focus:ring-2 focus:ring-inset text-sm sm:leading-6',
            _unref(errors).name
              ? 'text-red-900 ring-red-300 placeholder:text-red-300 focus:ring-red-500 pr-3'
              : 'text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-yellow-300 pr-10',
          ]
        }), null, 16), [
          [_vModelDynamic, _unref(nameFieldModel)]
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)("analytics.reports.filters")), 1),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)("analytics.reports.processes_components")), 1),
          _createElementVNode("div", {
            class: "cursor-pointer p-1 invisible group-hover/filter-box:visible rounded-md hover:bg-red-200 active:bg-red-300 transition-colors duration-100",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (handleRemoveFilter('processes')))
          }, [
            _createVNode(_unref(XMarkIcon), {
              class: "h-4 w-4 select-none",
              "aria-hidden": "true"
            })
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(t)("analytics.reports.is")), 1),
          _createVNode(ProcessPicker, {
            presets: [
              ...__props.hierarchyTags
                .filter((tag) => tag.type === 'component')
                .map((item) => ({ ...item, items: item.attached_processes || [], key: item._id })),
            ],
            selected: selectedProcesses.value,
            onOnSave: handleProcessesChange
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                class: _normalizeClass([
                'flex py-0.5 px-4 border rounded-full w-60 cursor-pointer',
                selectedProcessesLabel.value ? 'border-gray-300' : 'border-yellow-400',
              ])
              }, [
                (selectedProcessesLabel.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _createElementVNode("p", _hoisted_12, _toDisplayString(selectedProcessesLabel.value), 1)
                    ]))
                  : (_openBlock(), _createElementBlock("p", _hoisted_13, "..." + _toDisplayString(_unref(t)("analytics.reports.select_values")), 1))
              ], 2)
            ]),
            _: 1
          }, 8, ["presets", "selected"])
        ])
      ]),
      _createElementVNode("div", _hoisted_14, [
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("p", _hoisted_16, _toDisplayString(_unref(t)("analytics.reports.location")), 1),
          _createElementVNode("div", {
            class: "cursor-pointer p-1 invisible group-hover/filter-box:visible rounded-md hover:bg-red-200 active:bg-red-300 transition-colors duration-100",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (handleRemoveFilter('section_masks')))
          }, [
            _createVNode(_unref(XMarkIcon), {
              class: "h-4 w-4 select-none",
              "aria-hidden": "true"
            })
          ])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("span", _hoisted_18, _toDisplayString(_unref(t)("analytics.reports.is")), 1),
          _createVNode(ProcessLocationFilters, {
            class: "lg:w-72 mt-2 lg:mt-0",
            "onUpdate:selected": handleSectionMasksChange,
            selected: selectedSectionMasks.value,
            groups: tagGroups.value,
            changeOnSubmit: "",
            popoverClass: "translate-x-0 lg:translate-x-0 xl:-translate-x-8 md:right-auto",
            gridBoxClass: "xl:!grid-cols-2 2xl:!grid-cols-3"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                class: _normalizeClass([
                'flex py-0.5 px-4 border rounded-full w-60 cursor-pointer',
                Object.values(selectedSectionMasks.value).some((mask) => mask.length)
                  ? 'border-gray-300'
                  : 'border-yellow-400',
              ])
              }, [
                (Object.values(selectedSectionMasks.value).some((mask) => mask.length))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                      _createElementVNode("p", _hoisted_20, _toDisplayString(Object.values(selectedSectionMasks.value)
                      .flatMap((masks) => masks.map((item) => populateSectionMask(item)?.name))
                      .join(", ")), 1),
                      _createElementVNode("span", _hoisted_21, " (" + _toDisplayString(Object.values(selectedSectionMasks.value).reduce((acc, mask) => acc + mask.length, 0)) + ") ", 1)
                    ]))
                  : (_openBlock(), _createElementBlock("p", _hoisted_22, "..." + _toDisplayString(_unref(t)("analytics.reports.select_values")), 1))
              ], 2)
            ]),
            _: 1
          }, 8, ["selected", "groups"])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_23, [
      _createElementVNode("p", _hoisted_24, _toDisplayString(_unref(t)("analytics.reports.plot_config")), 1),
      (__props.selectedReport?.plot.type === 'cycle_times')
        ? (_openBlock(), _createBlock(CycleTimesConfig, {
            key: 0,
            hierarchyTags: props.hierarchyTags,
            report: __props.selectedReport,
            onConfigChange: handleReportChange,
            ref_key: "cycleTimesConfigComponent",
            ref: cycleTimesConfigComponent
          }, null, 8, ["hierarchyTags", "report"]))
        : _createCommentVNode("", true),
      (__props.selectedReport?.plot.type === 'working_hour_curve')
        ? (_openBlock(), _createBlock(WorkingHourCurveConfig, {
            key: 1,
            report: __props.selectedReport,
            onRefetch: handleReportRefetch,
            onConfigChange: handleReportChange,
            ref_key: "workingHourCurveConfigComponent",
            ref: workingHourCurveConfigComponent
          }, null, 8, ["report"]))
        : _createCommentVNode("", true)
    ])
  ], 2))
}
}

})